<template>
  <div>
    <div class="relative mx-auto">
      <Carousel
          v-if="gameItems.length > 0"
          :itemsToShow="carouselItemsToShow"
          :transition="500"
          :mouse-drag="true"
          :pauseAutoplayOnHover="true"
          :wrap-around="true"
          ref="carouselRef"
          @dragstart.prevent
          v-slot="{ currentSlide }"
      >
        <Slide
            v-for="(game, index) in gameItems"
            :key="index"
            v-slot="{ index: slideIndex }"
        >
          <article
              class="duration-300 transform transition-transform cursor-pointer overflow-hidden rounded-3xl"
              :class="[
              slideIndex === currentSlide
                ? 'scale-100 z-20'
                : 'scale-95 opacity-80 z-10'
            ]"
              @mousedown="isDragging = false"
              @mousemove="isDragging = true"
              @mouseup="handleClick(game.customUrl)"
          >
            <div class="bg-gray-800 bg-opacity-30 p-4 shadow-lg hover:shadow-2xl h-full w-full rounded-3xl overflow-hidden">
              <div class="relative flex items-end overflow-hidden h-96 w-full rounded-3xl">
                <img
                    :src="game.image"
                    alt="Game Photo"
                    class="w-full h-full object-cover"
                    loading="lazy"
                />
              </div>
              <div class="mt-4 p-2">
                <h2 class="text-slate-400 font-bold text-xl text-center">
                  {{ game.title }}
                </h2>
                <p class="text-sm text-slate-500 line-clamp-2 mt-2">
                  {{ game.description }}
                </p>
              </div>
            </div>
          </article>
        </Slide>
      </Carousel>

      <!-- Skeleton loader -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
            v-for="n in carouselItemsToShow"
            :key="n"
            class="rounded-xl bg-gray-800 bg-opacity-30 animate-pulse p-4 shadow-lg"
        >
          <div class="relative flex items-end overflow-hidden rounded-xl">
            <div class="h-72 bg-gray-600 rounded-xl mb-4 w-full"></div>
          </div>
          <div class="mt-1 p-2">
            <div class="h-4 bg-gray-600 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-600 rounded w-1/2"></div>
          </div>
        </div>
      </div>

      <!-- Setas -->
      <div v-if="gameItems.length > 0">
        <button
            @click="prevSlide"
            class="absolute top-1/2 left-0 transform -translate-y-1/2 bg-gray-900/70 text-white py-4 px-4 rounded-full z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <button
            @click="nextSlide"
            class="absolute top-1/2 right-0 transform -translate-y-1/2 bg-gray-900/70 text-white py-4 px-4 rounded-full z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>

      <!-- Fades laterais -->
      <div class="hidden lg:block pointer-events-none absolute top-0 left-0 w-32 h-full z-10 bg-gradient-to-r from-gray-900 to-transparent"></div>
      <div class="hidden lg:block pointer-events-none absolute top-0 right-0 w-32 h-full z-10 bg-gradient-to-l from-gray-900 to-transparent"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import Api from '@/services/api';

interface Page {
  customUrl: string;
  title: string;
  description: string;
  image: string;
  type: 'game' | 'blog' | 'project';
}

const pages = ref<Page[]>([]);
const carouselRef = ref<any>(null);
const carouselItemsToShow = ref(1);
const isLoading = ref(true);
const isDragging = ref(false);

const router = useRouter();

const gameItems = computed(() => pages.value);

const fetchPages = async () => {
  try {
    const projection = {
      customUrl: 1,
      title: 1,
      description: 1,
      image: 1,
    };
    const response = await Api.getPages({ type: 'game' }, projection);
    pages.value = response.data;
  } catch (error) {
    console.error('Error fetching pages:', error);
  } finally {
    isLoading.value = false;
  }
};

const updateItemsToShow = () => {
  const width = window.innerWidth;
  if (width < 640) {
    carouselItemsToShow.value = 1;
  } else if (width < 1024) {
    carouselItemsToShow.value = 2;
  } else {
    carouselItemsToShow.value = 4;
  }
};

const nextSlide = () => carouselRef.value?.next();
const prevSlide = () => carouselRef.value?.prev();

const handleClick = (url: string) => {
  if (!isDragging.value) {
    router.push(`/pages/${url}`);
  }
  isDragging.value = false;
};

onMounted(() => {
  fetchPages();
  updateItemsToShow();
  window.addEventListener('resize', updateItemsToShow);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateItemsToShow);
});
</script>

<style scoped>
.carousel__slide {
  padding: 5px;
  perspective: 2000px;
  transform-style: preserve-3d;
  transition: 0.5s;
}
@media (max-width: 767px) {
  .carousel__slide--active {
    transform: scale(1.15);
  }
}
</style>
